import { ChevronRight } from 'lucide-react';

import { useI18n } from '../i18n/I18n.js';
import { Notice } from '../ui/parts.js';
import { type Me, openable, type School } from './choose.js';

/**
 * The school picker, a first-class screen (`docs/product/admin-web-prd.md` §4). Schools the person
 * may open are buttons; the ones where they only teach or dance are listed, not openable, so a
 * school never silently vanishes from their own list.
 */
export function Picker({ me, onPick }: { me: Me; onPick: (tenantId: string) => void }) {
  const { t } = useI18n();
  const ordered = [...me.schools].sort((a, b) => Number(openable(b)) - Number(openable(a)));

  return (
    <Notice title={t.picker.title}>
      <p className="notice-text">{t.picker.body}</p>
      <ul className="schools">
        {ordered.map((school) => (
          <li key={school.tenantId}>
            {openable(school) ? (
              <button type="button" className="school" onClick={() => onPick(school.tenantId)}>
                <SchoolRow school={school} />
                <ChevronRight className="school-go" size={20} strokeWidth={1.5} aria-hidden />
              </button>
            ) : (
              <div className="school school-off">
                <SchoolRow school={school} note={t.picker.notHere} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

function SchoolRow({ school, note }: { school: School; note?: string }) {
  const { m, locale } = useI18n();
  const named = school.roles.map((role) => m.roles[role]).join(', ');
  const roles = named.charAt(0).toLocaleUpperCase(locale) + named.slice(1);
  return (
    <>
      <span className="school-initial" aria-hidden>
        {school.name.trim().charAt(0).toLocaleUpperCase()}
      </span>
      <span className="school-text">
        <span className="school-name">{school.name}</span>
        <span className="school-role">{note === undefined ? roles : `${roles} · ${note}`}</span>
      </span>
    </>
  );
}
